import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Navbar.css';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const { t, setLanguage, language } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMasarTechPage = location.pathname === '/masar-tech';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, target) => {
    setIsMobileMenuOpen(false);
    if (isMasarTechPage) {
      if (target.startsWith('/#')) {
        const sectionId = target.split('#')[1];
        const element = document.getElementById(sectionId);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
      return;
    }
    if (!isMasarTechPage) {
      if (target === '/') {
        e.preventDefault();
        const element = document.getElementById('hero');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else if (target.startsWith('/#')) {
        const sectionId = target.split('#')[1];
        const element = document.getElementById(sectionId);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const handleLogoClick = (e) => {
    if (isMasarTechPage) {
      e.preventDefault();
      navigate('/');
    } else {
      const element = document.getElementById('hero');
      if (element) {
        e.preventDefault();
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleLanguageSelect = (lang) => {
    setLanguage(lang);
    setIsLanguageMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isLanguageMenuOpen && !event.target.closest('.language-selector')) {
        setIsLanguageMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLanguageMenuOpen]);

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <div className="navbar-content">
          <div className="navbar-logo" onClick={handleLogoClick} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleLogoClick(e)}>
            <img
              src="/logo_navbar.png"
              alt={language === 'ar' ? 'مسار' : 'MASAR'}
              className="logo-image"
            />
            <div className="navbar-wordmark">
              <span className="navbar-wordmark__text">
                {language === 'ar' ? 'مسار' : 'MASAR'}
              </span>
            </div>
          </div>
          <div className={`navbar-menu ${isMobileMenuOpen ? 'active' : ''}`}>
            <Link to="/" onClick={(e) => handleNavClick(e, '/')}>{t.nav.home}</Link>
            <Link to="/#projects" onClick={(e) => handleNavClick(e, '/#projects')}>{t.nav.portfolio}</Link>
            <Link
              to="/masar-tech"
              className={`navbar-link navbar-link--masar-tech${isMasarTechPage ? ' is-active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="navbar-link--masar-tech__text">{t.nav.masarTech}</span>
              <i
                className={`fas fa-arrow-${language === 'ar' ? 'left' : 'right'} navbar-link--masar-tech__icon`}
                aria-hidden
              />
            </Link>
            <Link to="/#about" onClick={(e) => handleNavClick(e, '/#about')}>{t.nav.about}</Link>
            <Link to="/#contact" onClick={(e) => handleNavClick(e, '/#contact')}>{t.nav.contact}</Link>
          </div>
          <div className="navbar-actions">
            <button 
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label="Toggle theme"
              title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              <i className={`fas fa-${theme === 'light' ? 'moon' : 'sun'}`}></i>
            </button>
            <div className="language-selector">
              <button 
                className="language-toggle"
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                aria-label="Select language"
              >
                {language === 'ar' ? 'العربية' : 'English'}
                <i className={`fas fa-chevron-${isLanguageMenuOpen ? 'up' : 'down'} language-chevron`}></i>
              </button>
              {isLanguageMenuOpen && (
                <div className="language-dropdown">
                  <button
                    className={`language-option ${language === 'ar' ? 'active' : ''}`}
                    onClick={() => handleLanguageSelect('ar')}
                  >
                    <span>العربية</span>
                    {language === 'ar' && <i className="fas fa-check"></i>}
                  </button>
                  <button
                    className={`language-option ${language === 'en' ? 'active' : ''}`}
                    onClick={() => handleLanguageSelect('en')}
                  >
                    <span>English</span>
                    {language === 'en' && <i className="fas fa-check"></i>}
                  </button>
                </div>
              )}
            </div>
            <button 
              className={`navbar-toggle ${isMobileMenuOpen ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;

